import React, { useMemo, useState } from "react";

import {
  isFeatureActiveOnCurrentFarm,
  useFeatureFlag,
  useGetAllUserVerificationFarmTasks,
  useGetDataSubmissionDates,
  useGetFarmReportingYear,
  useGetFarms,
  useNavigate,
  useTranslation,
} from "utils";

import { FEATURE_FLAGS, FEATURE_FLAG_SLUGS, HISTORICAL_DATA_ROUTE } from "consts";

import { ACCOUNTING_METHODOLOGY, CropsWithMissingHistoricYieldDataByCropId, FieldListField } from "models";

import { Button, CounterBadge, Drawer, ErrorBoundary, Flex, Icon, Link, Typography } from "components";

import { DataSubmissionBarConfirmationModal } from "pages/app/all-field-view/components/DataSubmissionBarConfirmationModal";
import { BankingDetailsReviewDrawer } from "pages/app/all-field-view/components/tasksDrawer/BankingDetailsReviewDrawer";
import { CoverCropDetectionDrawer } from "pages/app/all-field-view/components/tasksDrawer/CoverCropDetectionDrawer";
import { FarmSourcesDrawer } from "pages/app/all-field-view/components/tasksDrawer/FarmSourcesDrawer";
import { MissingDataTaskDrawer } from "pages/app/all-field-view/components/tasksDrawer/MissingDataTaskDrawer";
import { YieldTaskDrawer } from "pages/app/all-field-view/components/tasksDrawer/YieldTaskDrawer";
import { TaskOverviewDrawer } from "pages/app/farm/components/TaskOverviewDrawer";
import { RedzoneConfirmationDrawer } from "pages/app/fields/components/RedzoneConfirmationDrawer";

type TasksDrawerProps = {
  visible: boolean;
  onClose: () => void;
  openMissingDataTasksCount: number;
  openMissingDocumentsCount: number;
  openMissingHistoricalPracticeDataTasksCount: number;
  cropsWithMissingHistoricYieldDataByCropId: CropsWithMissingHistoricYieldDataByCropId;
  missingHistoricYieldDataOpenTasksCount: number;
  onStartMissingDataTask?: () => void;
  onSubmitClick: () => Promise<void>;
  hasSelfProducedOrganicFertilizers?: boolean;
  openFarmSourcesTasksCount?: number;
  openCoverCropDetectionsCount?: number;
  isRedzoneTaskActive?: boolean;
  fieldsWithOutstandingRedzones?: FieldListField[];
  isRetentionFarm: boolean;
  isBankingDetailsTaskActive?: boolean;
  openBankingDetailsTasksCount?: number;
};

type DrawerTask = {
  key: string;
  openTasksCount: number;
  onClick: () => void;
  iconName?: string;
  defaultTitle?: string;
  defaultDescription?: string;
};

const TaskItem = ({
  title,
  description,
  openTasksCount,
  onClick,
  iconName,
}: {
  title: string;
  description: string;
  openTasksCount: number;
  onClick: () => void;
  iconName?: string;
}) => (
  <Flex align="center" justify="space-between" gap="1rem" className="task-item-container" onClick={onClick}>
    <Flex vertical>
      <Typography variant="paragraph-m" bold className="margin-0">
        {title}
      </Typography>
      <Typography variant="paragraph-m" color="grey-600" className="margin-0">
        {description}
      </Typography>
    </Flex>
    <Flex align="center" gap="0.5rem">
      {iconName && <Icon iconName={iconName} className="task-item-icon" size="m" />}
      {openTasksCount > 0 ? <CounterBadge count={openTasksCount} /> : <Icon iconName="tabler/circle-check" className="task-complete-icon" size="m" />}
      <Icon iconName="tabler/chevron-right" />
    </Flex>
  </Flex>
);

export const TasksDrawer = ({
  visible,
  onClose,
  onStartMissingDataTask,
  openMissingDataTasksCount,
  openMissingDocumentsCount,
  openMissingHistoricalPracticeDataTasksCount,
  cropsWithMissingHistoricYieldDataByCropId,
  missingHistoricYieldDataOpenTasksCount,
  onSubmitClick,
  hasSelfProducedOrganicFertilizers = false,
  openFarmSourcesTasksCount = 0,
  openCoverCropDetectionsCount = 0,
  isRedzoneTaskActive = false,
  fieldsWithOutstandingRedzones = [],
  isRetentionFarm,
  isBankingDetailsTaskActive = false,
  openBankingDetailsTasksCount = 0,
}: TasksDrawerProps) => {
  const { t } = useTranslation("allFieldView");
  const { currentFarm } = useGetFarms();
  const navigate = useNavigate();
  const { data: dataSubmissionDates, isLoading: isDataSubmissionDatesLoading } = useGetDataSubmissionDates({ farmId: currentFarm?.id });
  const {
    data: { activeFarmReportingYear },
    isLoading: isFarmReportingYearLoading,
  } = useGetFarmReportingYear(currentFarm?.id || null);

  const { isEnabled: isOrganicFertilizerSourcesEnabled } = useFeatureFlag(FEATURE_FLAGS.ORGANIC_FERTILIZER_SOURCES);

  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showYieldTaskDrawer, setShowYieldTaskDrawer] = useState(false);
  const [showMissingDataDrawer, setShowMissingDataDrawer] = useState(false);
  const [showMissingDocumentsDrawer, setShowMissingDocumentsDrawer] = useState<boolean>(false);
  const [showFarmSourcesDrawer, setShowFarmSourcesDrawer] = useState(false);
  const [showCoverCropDetectionDrawer, setShowCoverCropDetectionDrawer] = useState(false);
  const [showRedzoneDrawer, setShowRedzoneDrawer] = useState(false);
  const [showBankingDetailsDrawer, setShowBankingDetailsDrawer] = useState(false);
  const [areFinishedTasksShown, setAreFinishedTasksShown] = useState<boolean>(true);

  const openRedzoneTasksCount = isRedzoneTaskActive ? fieldsWithOutstandingRedzones.length : 0;

  const hasOpenTasks =
    openMissingDataTasksCount > 0 ||
    openRedzoneTasksCount > 0 ||
    openMissingDocumentsCount > 0 ||
    openBankingDetailsTasksCount > 0 ||
    (isSoilPlus3DataGapFeatureActive ? missingHistoricYieldDataOpenTasksCount > 0 : false);

  const isReadyToSubmit = !hasOpenTasks && !!activeFarmReportingYear && dataSubmissionDates?.isSubmissionPeriodOpen;

  const {
    data: verificationFarmTasks,
    isLoading: isLoadingVerificationFarmTasks,
    isFetching: isFetchingVerificationFarmTasks,
    refetch: refetchVerificationFarmTasks,
  } = useGetAllUserVerificationFarmTasks(currentFarm?.id);

  const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  const tasks = useMemo<DrawerTask[]>(() => {
    const base: DrawerTask[] = [
      ...(isBankingDetailsTaskActive
        ? [
            {
              key: "bankingDetails",
              openTasksCount: openBankingDetailsTasksCount,
              onClick: () => setShowBankingDetailsDrawer(true),
            },
          ]
        : []),
      ...(!isInventoryProgram && isSoilPlus3DataGapFeatureActive && isRetentionFarm
        ? [
            {
              key: "historicalPracticeData",
              openTasksCount: openMissingHistoricalPracticeDataTasksCount,
              onClick: () => {
                navigate(HISTORICAL_DATA_ROUTE);
              },
            },
          ]
        : []),
      ...(isSoilPlus3DataGapFeatureActive && isRetentionFarm
        ? [
            {
              key: "yield",
              openTasksCount: missingHistoricYieldDataOpenTasksCount,
              onClick: () => setShowYieldTaskDrawer(true),
            },
          ]
        : []),
      // stays listed once complete, so finished documents can be reviewed
      ...((verificationFarmTasks?.length ?? 0) > 0
        ? [
            {
              key: "missingDocuments",
              openTasksCount: openMissingDocumentsCount,
              onClick: () => {
                setShowMissingDocumentsDrawer(true);
              },
            },
          ]
        : []),
      ...(isRedzoneTaskActive
        ? [
            {
              key: "redzone",
              openTasksCount: openRedzoneTasksCount,
              onClick: () => setShowRedzoneDrawer(true),
            },
          ]
        : []),
      ...[
        {
          key: "missingData",
          openTasksCount: openMissingDataTasksCount,
          onClick: () => setShowMissingDataDrawer(true),
        },
      ],
      ...(isOrganicFertilizerSourcesEnabled && hasSelfProducedOrganicFertilizers
        ? [
            {
              key: "farmSources",
              openTasksCount: openFarmSourcesTasksCount,
              onClick: () => setShowFarmSourcesDrawer(true),
            },
          ]
        : []),
      ...(openCoverCropDetectionsCount > 0
        ? [
            {
              key: "coverCropDetections",
              openTasksCount: openCoverCropDetectionsCount,
              iconName: "tabler/satellite",
              onClick: () => setShowCoverCropDetectionDrawer(true),
              defaultTitle: "NEW: We spotted potential cover crops",
              defaultDescription: "Add them or simply skip them, if there was none. Adding them could improve your results.",
            },
          ]
        : []),
    ];

    return base;
  }, [
    openCoverCropDetectionsCount,
    isSoilPlus3DataGapFeatureActive,
    isOrganicFertilizerSourcesEnabled,
    isRetentionFarm,
    missingHistoricYieldDataOpenTasksCount,
    openMissingDataTasksCount,
    openMissingDocumentsCount,
    verificationFarmTasks,
    isInventoryProgram,
    openMissingHistoricalPracticeDataTasksCount,
    hasSelfProducedOrganicFertilizers,
    openFarmSourcesTasksCount,
    isRedzoneTaskActive,
    openRedzoneTasksCount,
    isBankingDetailsTaskActive,
    openBankingDetailsTasksCount,
    navigate,
  ]);

  const openTasks = tasks.filter(({ openTasksCount }) => openTasksCount > 0);
  const finishedTasks = tasks.filter(({ openTasksCount }) => openTasksCount === 0);

  const renderTaskItems = (drawerTasks: DrawerTask[]) =>
    drawerTasks.map(({ defaultTitle, defaultDescription, ...task }) => (
      <TaskItem
        {...task}
        key={task.key}
        title={t(`tasksDrawer.tasks.${task.key}.banner.title`, { defaultValue: defaultTitle })}
        description={t(`tasksDrawer.tasks.${task.key}.banner.description`, { defaultValue: defaultDescription })}
      />
    ));

  return (
    <>
      <Drawer
        title={isReadyToSubmit ? t("submissionBar.status.submissionReady.title", { year: activeFarmReportingYear }) : t("tasksDrawer.title")}
        visible={visible}
        onClose={onClose}
        className="tasks-drawer"
        loading={isLoadingVerificationFarmTasks || isFetchingVerificationFarmTasks || isFarmReportingYearLoading || isDataSubmissionDatesLoading}
        footer={
          isReadyToSubmit ? (
            <Button type="primary" block disabled={isSubmitting} onClick={() => setShowConfirmationModal(true)} data-testid="submission-complete-drawer-submit">
              {t("submissionBar.submit")}
            </Button>
          ) : null
        }
      >
        {isReadyToSubmit ? (
          <Flex vertical gap="1rem">
            <Flex align="center" gap="0.5rem">
              <Icon iconName="tabler/circle-check-filled" />
              <Typography variant="paragraph-l" bold className="margin-0">
                {t("submissionBar.complete")}
              </Typography>
            </Flex>
            <Typography variant="paragraph-m" className="margin-0">
              {t("submissionBar.status.submissionReady.description")}
            </Typography>
          </Flex>
        ) : (
          <>
            <Flex vertical gap="1rem" className="tasks-drawer-header">
              <Typography variant="h1">{t("tasksDrawer.header.title")}</Typography>
              <Flex align="center" gap="0.5rem" className="tasks-drawer-info-banner">
                <Icon iconName="tabler/bulb" />
                <Typography variant="paragraph-m">{t("tasksDrawer.header.infoBannerText")}</Typography>
              </Flex>
            </Flex>
            <Flex vertical gap="0.5rem">
              {renderTaskItems(openTasks)}
              {finishedTasks.length > 0 && (
                <div className="tasks-drawer-finished-row">
                  <Link dataTestId="tasks-drawer-finished-toggle" type="primary-link" onClick={() => setAreFinishedTasksShown((wereShown) => !wereShown)}>
                    <Flex align="center" gap="0.375rem">
                      <Icon iconName={areFinishedTasksShown ? "tabler/chevron-down" : "tabler/chevron-right"} size="s" />
                      {t("tasksDrawer.finishedTasks")}
                    </Flex>
                  </Link>
                </div>
              )}
              {areFinishedTasksShown && renderTaskItems(finishedTasks)}
            </Flex>
          </>
        )}
      </Drawer>
      <YieldTaskDrawer
        visible={showYieldTaskDrawer}
        onClose={() => setShowYieldTaskDrawer(false)}
        cropsWithMissingHistoricYieldDataByCropId={cropsWithMissingHistoricYieldDataByCropId}
      />
      <MissingDataTaskDrawer
        visible={showMissingDataDrawer}
        onClose={() => setShowMissingDataDrawer(false)}
        onStart={() => {
          setShowMissingDataDrawer(false);
          onClose();
          onStartMissingDataTask?.();
        }}
      />
      <RedzoneConfirmationDrawer
        fields={fieldsWithOutstandingRedzones}
        visible={showRedzoneDrawer}
        onClose={() => setShowRedzoneDrawer(false)}
        onConfirm={() => setShowRedzoneDrawer(false)}
      />
      <BankingDetailsReviewDrawer visible={showBankingDetailsDrawer} onClose={() => setShowBankingDetailsDrawer(false)} />
      <FarmSourcesDrawer visible={showFarmSourcesDrawer} onClose={() => setShowFarmSourcesDrawer(false)} />
      <CoverCropDetectionDrawer visible={showCoverCropDetectionDrawer} onClose={() => setShowCoverCropDetectionDrawer(false)} farmId={currentFarm?.id} />
      <ErrorBoundary>
        <TaskOverviewDrawer
          farmTasks={verificationFarmTasks ?? []}
          visible={showMissingDocumentsDrawer}
          onClose={() => setShowMissingDocumentsDrawer(false)}
          onDataChange={refetchVerificationFarmTasks}
          loading={isLoadingVerificationFarmTasks || isFetchingVerificationFarmTasks}
        />
      </ErrorBoundary>
      <DataSubmissionBarConfirmationModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        onSubmitClick={async () => {
          await onSubmitClick();
          onClose();
        }}
      />
    </>
  );
};
